/* Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Inject, Injectable, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { NvHigConfig, NV_HIG_CONFIG } from '../../hig.config';
import { FocusManager } from '../../services/focus-manager';
import { NvHigBottomSheetComponent } from '../nv-hig-bottom-sheet/nv-hig-bottom-sheet.component';
import { NvHigDialogComponent } from '../nv-hig-dialog/nv-hig-dialog.component';
import { NvHigThreeQuarterSheetComponent } from '../nv-hig-three-quarter-sheet/nv-hig-three-quarter-sheet.component';
import { NvHigGenericDialogRef } from './nv-generic-dialog-ref';
import { NvGenericDialogConfig, OpenGenericDialogOptions, PreferredDialogType } from './nv-generic-dialog.types';

@Injectable({
    providedIn: 'root'
})
export class NvGenericDialog {
    private tvMode: boolean;

    constructor(
        private matDialog: MatDialog,
        private matBottomSheet: MatBottomSheet,
        private focusManager: FocusManager,
        private translateService: TranslateService,
        @Optional() @Inject(NV_HIG_CONFIG) config: NvHigConfig
    ) {
        this.tvMode = !!config.tvMode;
    }

    public open(config: NvGenericDialogConfig, options?: OpenGenericDialogOptions): NvHigGenericDialogRef {
        // autoFocus is true by default in MatDialogConfig
        // set autoFocus to false to avoid conflicting with nvAutoFocus
        config.autoFocus = false;
        // Accessibility policy:
        // Use `aria-label` consistently across all dialog types to provide
        // a direct accessible name
        const factory = () => {
            if (options?.preferredDialogType === PreferredDialogType.BottomSheet) {
                // BottomSheet: provide direct accessible name via aria-label
                // Pick first available key, then translate
                const labelKey = config.data?.headerText?.text || config.data?.bodyText?.text;
                config.ariaLabel = labelKey
                    ? this.translateService.instant(labelKey)
                    : this.translateService.instant('threeQuarterSheet.dialog');
                return NvHigBottomSheetComponent.open(this.matBottomSheet, config, this.tvMode);
            }
            if (this.tvMode) {
                if (
                    config.data.checkbox ||
                    config.data.radioButtonOptions ||
                    options?.preferredDialogType === PreferredDialogType.ThreeQuarterSheet
                ) {
                    // Three-quarter sheet MatDialog: provide accessible name via aria-label
                    const labelKey =
                        config.data?.threeQuarterSheetData?.title?.text ||
                        config.data?.headerText?.text ||
                        config.data?.bodyText?.text;
                    config.ariaLabel = labelKey
                        ? this.translateService.instant(labelKey)
                        : this.translateService.instant('threeQuarterSheet.dialog');
                    return NvHigThreeQuarterSheetComponent.open(this.matDialog, config);
                } else {
                    // TV BottomSheet: use aria-label (no aria-labelledby support)
                    // Translate the header or body text (in that order)
                    const labelKey = config.data?.headerText?.text || config.data?.bodyText?.text;
                    config.ariaLabel = labelKey
                        ? this.translateService.instant(labelKey)
                        : this.translateService.instant('threeQuarterSheet.dialog');
                    return NvHigBottomSheetComponent.open(this.matBottomSheet, config, this.tvMode);
                }
            } else {
                // Standard MatDialog: provide accessible name via aria-label
                const labelKey = config.data?.headerText?.text || config.data?.bodyText?.text;
                config.ariaLabel = labelKey
                    ? this.translateService.instant(labelKey)
                    : this.translateService.instant('threeQuarterSheet.dialog');
                return NvHigDialogComponent.open(this.matDialog, config);
            }
        };

        if (options?.disableFocusRestore) {
            return new NvHigGenericDialogRef(factory());
        }
        return this.focusManager.restoreFocusAfterClosed(() => new NvHigGenericDialogRef(factory()), options);
    }

    public closeAll() {
        this.matDialog.closeAll();
        this.matBottomSheet.dismiss();
    }
}
